import { api } from '@/lib/api';
import { unwrapApiData } from '@/lib/store-formation';
import type { SalesFunnel } from '@/lib/sales-funnel';

export type FunnelAiObjective = 'audience' | 'sell' | 'custom' | 'webinar';

export interface FunnelAiPlanStepSummary {
  title: string;
  goal: string;
  keyMessage: string;
}

export interface FunnelAiPlan {
  funnelName: string;
  templateId: string;
  templateName?: string;
  objective: FunnelAiObjective;
  rationale: string;
  marketingAngle: string;
  targetAudience: string;
  flow: {
    capture: FunnelAiPlanStepSummary;
    sales: FunnelAiPlanStepSummary;
    checkout: FunnelAiPlanStepSummary;
    thank_you: FunnelAiPlanStepSummary;
  };
  stepContent: {
    capture: Record<string, unknown>;
    sales: Record<string, unknown>;
    checkout: Record<string, unknown>;
    thank_you: Record<string, unknown>;
  };
}

export interface FormationAiContext {
  storeFormationId: string;
  flowId: string;
  title: string;
  description: string | null;
  price: number;
  category: string | null;
  coverImageUrl: string | null;
  objectives: string[];
  structureSummary: string;
  blocksSummary: string;
}

export async function analyzeFormationForFunnelAi(flowId: string): Promise<{
  context: FormationAiContext;
  understanding: string;
}> {
  const resp = await api.post('/funnels/ai/analyze', { flowId });
  return unwrapApiData(resp);
}

export async function proposeFunnelAiPlan(
  storeFormationId: string,
  understanding: string,
): Promise<{ plan: FunnelAiPlan; context: FormationAiContext }> {
  const resp = await api.post('/funnels/ai/propose', { storeFormationId, understanding });
  return unwrapApiData(resp);
}

export async function refineFunnelAiPlan(
  storeFormationId: string,
  understanding: string,
  plan: FunnelAiPlan,
  corrections: string,
): Promise<{ plan: FunnelAiPlan }> {
  const resp = await api.post('/funnels/ai/refine-plan', {
    storeFormationId,
    understanding,
    plan,
    corrections,
  });
  return unwrapApiData(resp);
}

export async function createFunnelFromAiPlan(
  storeFormationId: string,
  plan: FunnelAiPlan,
): Promise<SalesFunnel> {
  const resp = await api.post('/funnels/ai/create', { storeFormationId, plan });
  return unwrapApiData<SalesFunnel>(resp);
}
